import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/auth';
import { toast } from 'react-hot-toast';
import {
  User,
  LogOut,
  Shield,
  Calendar,
  ArrowLeft,
  Edit,
  Save,
  X,
  Bell,
  Volume2,
  VolumeX,
  Lock,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  FileSignature,
} from 'lucide-react';
import EmailSignatures from './EmailSignatures';
import { normalizeDomains } from '@/utils/domains';
import './ProfilePage.css';

interface UserProfile {
  id: string;
  username: string;
  pk: string;
  domains: string[];
  first_name: string;
  last_name: string;
  is_active: boolean | null;
  tfa_enabled: boolean | null;
  joinDate: string;
  lastLogin: string;
  notifications: {
    enabled: boolean;
    sound: boolean;
    desktop: boolean;
    newEmail: boolean;
    mentions: boolean;
  };
}

interface PasswordStrength {
  score: number;
  feedback: string;
  color: string;
}

type ProfileTab = 'profile' | 'security' | 'notifications' | 'signatures' | 'account';

const PROFILE_TABS: { id: ProfileTab; label: string; shortLabel: string; icon: typeof User }[] = [
  { id: 'profile', label: 'Profile', shortLabel: 'Profile', icon: User },
  { id: 'security', label: 'Security', shortLabel: 'Security', icon: Shield },
  { id: 'notifications', label: 'Notifications', shortLabel: 'Alerts', icon: Bell },
  { id: 'signatures', label: 'Signatures', shortLabel: 'Sign', icon: FileSignature },
  { id: 'account', label: 'Account', shortLabel: 'Account', icon: LogOut },
];

const checkPasswordStrength = (password: string): PasswordStrength => {
  let score = 0;

  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[a-z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  switch (true) {
    case score <= 2:
      return { score, feedback: 'Weak — add uppercase, numbers, or special characters', color: '#dc2626' };
    case score <= 4:
      return { score, feedback: 'Medium — add more character variety', color: '#eab308' };
    default:
      return { score, feedback: 'Strong — good password!', color: '#16a34a' };
  }
};

function ProfileSwitch({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <label className={`profile-switch${disabled ? ' profile-switch--disabled' : ''}`}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      <span className="profile-switch__track">
        <span className="profile-switch__thumb" />
      </span>
    </label>
  );
}

export function ProfilePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ProfileTab>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const currentUser = authService.getCurrentUser();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const [profile, setProfile] = useState<UserProfile>({
    id: currentUser?.id || '',
    username: currentUser?.username || 'User',
    pk: currentUser?.pk || '',
    domains: normalizeDomains(currentUser?.domains),
    first_name: currentUser?.first_name || '',
    last_name: currentUser?.last_name || '',
    is_active: currentUser?.is_active || false,
    tfa_enabled: currentUser?.tfa_enabled || false,
    joinDate: new Date().toLocaleDateString(),
    lastLogin: new Date().toLocaleDateString(),
    notifications: {
      enabled: true,
      sound: true,
      desktop: true,
      newEmail: true,
      mentions: true,
    },
  });

  const [editForm, setEditForm] = useState({
    first_name: profile.first_name,
    last_name: profile.last_name,
  });

  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    feedback: '',
    color: '#dc2626',
  });

  const displayName = `${profile.first_name} ${profile.last_name}`.trim() || profile.username;

  const handleLogout = async () => {
    try {
      toast.loading('Logging out...');
      await authService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout. Please try again.');
      navigate('/login', { replace: true });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditForm({
      first_name: profile.first_name,
      last_name: profile.last_name,
    });
  };

  const handleSave = async () => {
    try {
      await authService.updateProfile(profile.id, {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
      });

      setProfile((prev) => ({
        ...prev,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
      }));

      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile. Please try again.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      first_name: profile.first_name,
      last_name: profile.last_name,
    });
  };

  const handleInputChange = (field: keyof typeof editForm, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNotificationChange = (field: keyof UserProfile['notifications']) => {
    setProfile((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [field]: !prev.notifications[field],
      },
    }));
  };

  const handlePasswordChange = (field: keyof typeof passwordForm, value: string) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    setPasswordError('');
    if (field === 'newPassword') {
      setPasswordStrength(checkPasswordStrength(value));
    }
  };

  const togglePasswordVisibility = (field: keyof typeof showPassword) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    try {
      await authService.updatePassword({
        userId: profile.id,
        password: passwordForm.newPassword,
      });

      setPasswordForm({ newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
      toast.success('Password updated successfully');
    } catch (error) {
      console.error('Failed to update password:', error);
      toast.error('Failed to update password. Please try again.');
    }
  };

  const playNotificationSound = () => {
    if (!audioRef.current) return;

    if (!isPlaying) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
      audioRef.current.onended = () => setIsPlaying(false);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const renderProfileTab = () => (
    <div className="profile-card">
      <div className="profile-card__header">
        <div className="profile-card__title-row">
          <User size={18} className="profile-card__title-icon" />
          <h2 className="profile-card__title">Profile Information</h2>
        </div>
        <div className="profile-card__actions">
          {!isEditing ? (
            <button type="button" className="profile-btn profile-btn--ghost" onClick={handleEdit}>
              <Edit size={16} />
              Edit
            </button>
          ) : (
            <>
              <button type="button" className="profile-btn profile-btn--success" onClick={handleSave}>
                <Save size={16} />
                Save
              </button>
              <button type="button" className="profile-btn profile-btn--muted" onClick={handleCancel}>
                <X size={16} />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="profile-grid">
        <div className="profile-field">
          <label className="profile-field__label">First Name</label>
          {isEditing ? (
            <input
              type="text"
              className="profile-field__input"
              value={editForm.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              placeholder="Enter your first name"
            />
          ) : (
            <div className="profile-field__value">{profile.first_name || 'Not set'}</div>
          )}
        </div>

        <div className="profile-field">
          <label className="profile-field__label">Last Name</label>
          {isEditing ? (
            <input
              type="text"
              className="profile-field__input"
              value={editForm.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              placeholder="Enter your last name"
            />
          ) : (
            <div className="profile-field__value">{profile.last_name || 'Not set'}</div>
          )}
        </div>

        <div className="profile-field">
          <label className="profile-field__label">Username</label>
          <div className="profile-field__value" title={profile.username}>
            {profile.username}
          </div>
        </div>

        <div className="profile-field">
          <label className="profile-field__label">Member Since</label>
          <div className="profile-field__value">
            <Calendar size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
            {profile.joinDate}
          </div>
        </div>

        <div className="profile-field profile-field--full">
          <label className="profile-field__label">Domains</label>
          <div className="profile-field__value" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
            {profile.domains.length > 0 ? (
              profile.domains.map((domain) => (
                <span key={domain} className="profile-domain-badge">
                  {domain}
                </span>
              ))
            ) : (
              <span style={{ color: '#5f6368' }}>No domains</span>
            )}
          </div>
        </div>

        <div className="profile-field">
          <label className="profile-field__label">Account Status</label>
          <div
            className={`profile-field__value profile-status ${
              profile.is_active ? 'profile-status--active' : 'profile-status--inactive'
            }`}
          >
            <span
              className={`profile-status__dot ${
                profile.is_active ? 'profile-status__dot--active' : 'profile-status__dot--inactive'
              }`}
            />
            {profile.is_active ? 'Active' : 'Inactive'}
          </div>
        </div>

        <div className="profile-field">
          <label className="profile-field__label">Two-Factor Authentication</label>
          <div className="profile-field__value">
            <Shield size={16} style={{ color: profile.tfa_enabled ? '#10b981' : '#9ca3af', flexShrink: 0 }} />
            {profile.tfa_enabled ? 'Enabled' : 'Disabled'}
          </div>
        </div>

        <div className="profile-field">
          <label className="profile-field__label">Last Login</label>
          <div className="profile-field__value">
            <Calendar size={16} style={{ color: '#9ca3af', flexShrink: 0 }} />
            {profile.lastLogin}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <>
      <div className="profile-card">
        <div className="profile-card__header">
          <div className="profile-card__title-row">
            <Lock size={18} className="profile-card__title-icon" />
            <h2 className="profile-card__title">Password</h2>
          </div>
          {!showPasswordForm && (
            <button
              type="button"
              className="profile-btn profile-btn--ghost"
              onClick={() => setShowPasswordForm(true)}
            >
              <Edit size={16} />
              Change
            </button>
          )}
        </div>

        {showPasswordForm ? (
          <form onSubmit={handlePasswordSubmit}>
            {passwordError && (
              <div className="profile-alert">
                <AlertCircle size={16} />
                {passwordError}
              </div>
            )}

            <div className="profile-field" style={{ marginBottom: 16 }}>
              <label className="profile-field__label">New Password</label>
              <div className="profile-password-wrap">
                <input
                  type={showPassword.newPassword ? 'text' : 'password'}
                  className="profile-field__input"
                  value={passwordForm.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  className="profile-password-toggle"
                  onClick={() => togglePasswordVisibility('newPassword')}
                >
                  {showPassword.newPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {passwordForm.newPassword && (
                <div>
                  <div className="profile-strength-meta">
                    <div className="profile-strength-bar">
                      <div
                        className="profile-strength-bar__fill"
                        style={{
                          width: `${(passwordStrength.score / 6) * 100}%`,
                          backgroundColor: passwordStrength.color,
                        }}
                      />
                    </div>
                    {passwordStrength.score >= 5 && (
                      <span style={{ color: passwordStrength.color }}>
                        <Check size={14} />
                      </span>
                    )}
                  </div>
                  <p className="profile-strength-feedback">{passwordStrength.feedback}</p>
                </div>
              )}
            </div>

            <div className="profile-field">
              <label className="profile-field__label">Confirm Password</label>
              <div className="profile-password-wrap">
                <input
                  type={showPassword.confirmPassword ? 'text' : 'password'}
                  className="profile-field__input"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  className="profile-password-toggle"
                  onClick={() => togglePasswordVisibility('confirmPassword')}
                >
                  {showPassword.confirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="profile-form-actions">
              <button
                type="button"
                className="profile-btn profile-btn--muted"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordForm({ newPassword: '', confirmPassword: '' });
                  setPasswordError('');
                }}
              >
                Cancel
              </button>
              <button type="submit" className="profile-btn profile-btn--primary">
                <Save size={16} />
                Update Password
              </button>
            </div>
          </form>
        ) : (
          <p className="profile-card__desc" style={{ margin: 0 }}>
            Use a strong password with at least 8 characters. Change it regularly to keep your account secure.
          </p>
        )}
      </div>

      <div className="profile-card">
        <div className="profile-card__title-row" style={{ marginBottom: 12 }}>
          <Shield size={18} className="profile-card__title-icon" />
          <h2 className="profile-card__title">Two-Factor Authentication</h2>
        </div>
        <div className="profile-field__value">
          <Shield size={16} style={{ color: profile.tfa_enabled ? '#10b981' : '#9ca3af', flexShrink: 0 }} />
          {profile.tfa_enabled ? 'Two-factor authentication is enabled on your account.' : 'Two-factor authentication is not enabled.'}
        </div>
      </div>
    </>
  );

  const renderNotificationsTab = () => (
    <div className="profile-card">
      <div className="profile-card__title-row" style={{ marginBottom: 20 }}>
        <Bell size={18} className="profile-card__title-icon" />
        <h2 className="profile-card__title">Notification Settings</h2>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          className={`profile-setting-row${
            profile.notifications.enabled ? ' profile-setting-row--highlight' : ''
          }`}
        >
          <div className="profile-setting-row__text">
            <h3 className="profile-setting-row__title">Enable Notifications</h3>
            <p className="profile-setting-row__desc">
              Receive notifications about new emails and mentions
            </p>
          </div>
          <ProfileSwitch
            checked={profile.notifications.enabled}
            onChange={() => handleNotificationChange('enabled')}
          />
        </div>

        <div className="profile-setting-row">
          <div className="profile-setting-row__text" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {profile.notifications.sound ? (
              <Volume2 size={20} style={{ color: '#8d00e4', flexShrink: 0 }} />
            ) : (
              <VolumeX size={20} style={{ color: '#5f6368', flexShrink: 0 }} />
            )}
            <div>
              <h3 className="profile-setting-row__title">Notification Sound</h3>
              <p className="profile-setting-row__desc">Play a sound when receiving notifications</p>
            </div>
          </div>
          <ProfileSwitch
            checked={profile.notifications.sound}
            onChange={() => handleNotificationChange('sound')}
          />
        </div>

        <div className="profile-sound-test">
          <button
            type="button"
            className="profile-btn profile-btn--test"
            onClick={playNotificationSound}
            disabled={!profile.notifications.sound}
          >
            {isPlaying ? 'Stop' : 'Test'} Sound
          </button>
          <span className="profile-sound-test__hint">
            {profile.notifications.sound
              ? 'Click to preview the notification sound'
              : 'Enable notification sound to test'}
          </span>
        </div>

        {(
          [
            { key: 'desktop' as const, label: 'Desktop Notifications', description: 'Show notifications on your desktop' },
            { key: 'newEmail' as const, label: 'New Email Notifications', description: 'Get notified when you receive new emails' },
            { key: 'mentions' as const, label: 'Mention Notifications', description: 'Get notified when you are mentioned' },
          ] as const
        ).map(({ key, label, description }) => (
          <div key={key} className="profile-setting-row">
            <div className="profile-setting-row__text">
              <h3 className="profile-setting-row__title">{label}</h3>
              <p className="profile-setting-row__desc">{description}</p>
            </div>
            <ProfileSwitch
              checked={profile.notifications[key]}
              onChange={() => handleNotificationChange(key)}
              disabled={!profile.notifications.enabled}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderAccountTab = () => (
    <div className="profile-card profile-card--danger">
      <h2 className="profile-card__title">Sign Out</h2>
      <p className="profile-card__desc">
        Once you log out, you&apos;ll need to sign in again to access your account.
      </p>
      <button type="button" className="profile-btn profile-btn--danger" onClick={handleLogout}>
        <LogOut size={18} />
        Log Out
      </button>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab();
      case 'security':
        return renderSecurityTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'signatures':
        return <EmailSignatures />;
      case 'account':
        return renderAccountTab();
      default:
        return null;
    }
  };

  return (
    <div className="profile-page">
      <header className="profile-page__header">
        <button
          type="button"
          className="profile-page__back-btn"
          onClick={() => navigate(-1)}
          title="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="profile-page__title">Profile Settings</h1>
      </header>

      <div className="profile-page__scroll">
        <div className="profile-page__hero">
          <div className="profile-page__avatar">
            <User size={28} />
          </div>
          <div className="profile-page__hero-text">
            <h2 className="profile-page__name">{displayName}</h2>
            <p className="profile-page__username">{profile.username}</p>
          </div>
        </div>

        <nav className="profile-page__tabs" role="tablist" aria-label="Profile sections">
          {PROFILE_TABS.map(({ id, label, shortLabel, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeTab === id}
              className={`profile-page__tab${activeTab === id ? ' profile-page__tab--active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={16} />
              <span className="profile-page__tab-label--full">{label}</span>
              <span className="profile-page__tab-label--short">{shortLabel}</span>
            </button>
          ))}
        </nav>

        <div className="profile-page__body">
          <div className="profile-page__panel" role="tabpanel">
            {renderActiveTab()}
          </div>
        </div>
      </div>

      <audio ref={audioRef} src="/notification.mp3" />
    </div>
  );
}
